import {
   Badge,
   Container,
   Group,
   Loader,
   Pagination,
   Paper,
   Stack,
   Text,
   ThemeIcon,
   Title,
   TextInput,
   Menu,
   ActionIcon,
   Tabs,
   Table,
   ScrollArea,
   Modal,
   Button,
   Divider,
   Grid,
   Box,
} from "@mantine/core";
import {
   IconCreditCard,
   IconCheck,
   IconX,
   IconSearch,
   IconDots,
   IconEye,
   IconClock,
   IconCurrencyNaira,
   IconUser,
   IconPhone,
   IconMail,
   IconCalendar,
   IconFileText,
   IconTrendingUp,
   IconBuildingBank,
   IconWallet,
   IconShieldCheck,
   IconAlertCircle,
   IconLock,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { usePerformRequest } from "../../lib/usePerformRequest";
import { IPaginationProps, PaginatedResponse } from "../../types";
import { ILoanRequestProps, ILoanRequestResponseProps } from "../../types/Loan";
import { Endpoints } from "../../lib/endpoints";
import { useToastNotification } from "../../hooks/useToastNotification";
import { PerformRequest } from "../../lib/usePerformRequest";
import { HttpCodes } from "../../lib/data";
import { useNavigate } from "react-router-dom";
import { LoanRequestStatus } from "../../types/Loan";
import { getEllipsisFromString } from "../../lib/methods";

export default function LoanRequests() {
   const navigate = useNavigate();
   const [searchQuery, setSearchQuery] = useState("");
   const [activeTab, setActiveTab] = useState<string | null>("all");
   const [pagination, setPagination] = useState<IPaginationProps>({
      page: 1,
      limit: 10,
   });
   const [updatingLoanId, setUpdatingLoanId] = useState<string | null>(null);
   const [selectedLoan, setSelectedLoan] = useState<ILoanRequestResponseProps | null>(null);
   const [modalOpened, setModalOpened] = useState(false);
   const [approvalModalOpened, setApprovalModalOpened] = useState(false);
   const [loanToApprove, setLoanToApprove] = useState<ILoanRequestResponseProps | null>(null);
   const [isDisbursing, setIsDisbursing] = useState(false);
   const [requiresOtp, setRequiresOtp] = useState(false);
   const [otp, setOtp] = useState("");
   const [transferCode, setTransferCode] = useState<string | null>(null);
   const [directDebitAmount, setDirectDebitAmount] = useState<string>("");
   const [isSettingDirectDebit, setIsSettingDirectDebit] = useState(false);
   const { success, error } = useToastNotification();

   const {
      data: loansResponse,
      isLoading,
      reloadData: reloadLoans,
   } = usePerformRequest<PaginatedResponse<ILoanRequestResponseProps[]>>({
      method: Endpoints.GetLoanRequests(
         pagination.page,
         pagination.limit,
         activeTab === "all" ? undefined : (activeTab as any)
      ).method,
      route: Endpoints.GetLoanRequests(
         pagination.page,
         pagination.limit,
         activeTab === "all" ? undefined : (activeTab as any)
      ).route,
   });

   useEffect(() => {
      reloadLoans();
   }, [pagination, activeTab]);

   const formatDate = (timestamp: number) => {
      return new Date(timestamp).toLocaleDateString("en-US", {
         year: "numeric",
         month: "short",
         day: "numeric",
         hour: "2-digit",
         minute: "2-digit",
      });
   };

   const formatCurrency = (amount: number) => {
      return `₦${amount.toLocaleString("en-NG")}`;
   };

   const getStatusColor = (status: string) => {
      switch (status) {
         case "approved":
            return "green";
         case "rejected":
            return "red";
         case "disbursed":
            return "blue";
         default:
            return "yellow";
      }
   };

   const getStatusIcon = (status: string) => {
      switch (status) {
         case "approved":
            return IconCheck;
         case "rejected":
            return IconX;
         default:
            return IconClock;
      }
   };

   const totalPages = loansResponse ? Math.ceil(loansResponse.totalRecords / loansResponse.limit) : 1;

   // Calculate stats
   const stats = {
      total: loansResponse?.totalRecords || 0,
      pending: 0, // These would come from the API
      approved: 0,
      rejected: 0,
      totalAmount: 0,
   };

   const handleViewDetails = (loan: ILoanRequestResponseProps) => {
      setSelectedLoan(loan);
      setModalOpened(true);
   };

   const handleOpenApproval = (loan: ILoanRequestResponseProps) => {
      setLoanToApprove(loan);
      setApprovalModalOpened(true);
      setRequiresOtp(false);
      setOtp("");
      setTransferCode(null);
   };

   const handleApproveLoan = async () => {
      if (!loanToApprove) return;

      // Check if user has bank account
      const user = loanToApprove.userID as any;
      if (!user?.bankAccount || !user?.bankAccount?.accountNumber || !user?.bankAccount?.bankCode) {
         error(
            "Cannot approve loan: User has not set up a bank account. Please ask the user to add their bank account details first."
         );
         setApprovalModalOpened(false);
         return;
      }

      setUpdatingLoanId(loanToApprove.id);
      try {
         const response = await PerformRequest({
            method: Endpoints.UpdateLoanRequestStatus(loanToApprove.id, "approved").method,
            route: Endpoints.UpdateLoanRequestStatus(loanToApprove.id, "approved").route,
            data: { status: "approved" },
         });

         if (response && response.status === HttpCodes.Success) {
            success("Loan approved successfully!");
            // Update local loan state to reflect approval
            if (loanToApprove) {
               setLoanToApprove({ ...loanToApprove, status: "approved" as LoanRequestStatus });
            }
            await reloadLoans();
            setApprovalModalOpened(false);
            setLoanToApprove(null);
         } else {
            error(response.message || "Failed to approve loan");
            setApprovalModalOpened(false);
         }
      } catch (err: any) {
         if (err.message?.includes("bank account")) {
            error(err.message);
         } else {
            error(err.message || "Failed to approve loan");
         }
         setApprovalModalOpened(false);
      } finally {
         setUpdatingLoanId(null);
      }
   };

   const handleDisburseLoan = async () => {
      if (!loanToApprove) return;

      setIsDisbursing(true);
      try {
         const response = await PerformRequest<{
            success?: boolean;
            requiresOtp?: boolean;
            transferCode?: string;
            message?: string;
         }>({
            method: Endpoints.DisburseLoan(loanToApprove.id).method,
            route: Endpoints.DisburseLoan(loanToApprove.id).route,
            data: otp ? { otp } : {},
         });

         if (response && response.status === HttpCodes.Success) {
            const result = response.data as
               | {
                    success?: boolean;
                    requiresOtp?: boolean;
                    transferCode?: string;
                    message?: string;
                 }
               | undefined;

            if (result?.requiresOtp && !otp) {
               setRequiresOtp(true);
               setTransferCode(result.transferCode || null);
               error("OTP required to finalize transfer. Please check your Paystack account for the OTP.");
               setIsDisbursing(false);
               return;
            }

            if (result?.success) {
               success(result.message || "Loan disbursed successfully!");
               await reloadLoans();
               setApprovalModalOpened(false);
               setLoanToApprove(null);
               setRequiresOtp(false);
               setOtp("");
               setTransferCode(null);
            } else {
               error(result?.message || "Transfer is being processed. Please check the status later.");
            }
         } else {
            error((response as any)?.message || "Failed to disburse loan");
         }
      } catch (err: any) {
         error(err.message || "Failed to disburse loan");
      } finally {
         setIsDisbursing(false);
      }
   };

   const handleSetDirectDebitAmount = async () => {
      if (!loanToApprove) return;

      const raw = directDebitAmount.replace(/,/g, "").trim();
      const value = Number(raw);

      if (!raw || isNaN(value) || value <= 0) {
         error("Please enter a valid direct debit amount greater than zero.");
         return;
      }

      setIsSettingDirectDebit(true);
      try {
         const response = await PerformRequest<{
            scheduledDirectDebitAmount: number;
            outstandingBalance: number;
         }>({
            method: Endpoints.SetLoanDirectDebitAmount(loanToApprove.id).method,
            route: Endpoints.SetLoanDirectDebitAmount(loanToApprove.id).route,
            data: { amount: value },
         });

         if (response && response.status === HttpCodes.Success) {
            const result = response.data as any;
            success(
               `Direct debit amount of ${formatCurrency(
                  result?.scheduledDirectDebitAmount ?? value
               )} has been scheduled.`,
            );
         } else {
            error((response as any)?.message || "Failed to set direct debit amount.");
         }
      } catch (err: any) {
         error(err?.message || "Failed to set direct debit amount.");
      } finally {
         setIsSettingDirectDebit(false);
      }
   };

   const handleUpdateLoanStatus = async (loanId: string, status: LoanRequestStatus) => {
      if (status === "approved") {
         // For approval, use the new approval modal
         const loan = loansResponse?.data?.find((l) => l.id === loanId);
         if (loan) {
            handleOpenApproval(loan);
         }
         return;
      }

      // For rejection, use the old flow
      setUpdatingLoanId(loanId);
      try {
         const response = await PerformRequest({
            method: Endpoints.UpdateLoanRequestStatus(loanId, status).method,
            route: Endpoints.UpdateLoanRequestStatus(loanId, status).route,
            data: { status },
         });

         if (response && response.status === HttpCodes.Success) {
            success(`Loan ${status} successfully!`);
            await reloadLoans();
            setModalOpened(false);
         } else {
            error(response?.message || `Failed to ${status} loan`);
         }
      } catch (err: any) {
         error(err.message || `Failed to ${status} loan`);
      } finally {
         setUpdatingLoanId(null);
      }
   };

   return (
      <Container size="xl" py="xl">
         <Stack gap="xl">
            {/* Header Section */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
               <Group justify="space-between" align="flex-end" mb="md">
                  <div>
                     <Title order={1} size="3rem" fw={800} style={{ color: "#679369", marginBottom: "0.5rem" }}>
                        Loan Requests
                     </Title>
                     <Text size="lg" c="dimmed">
                        Review and manage all loan applications
                     </Text>
                  </div>
               </Group>

               {/* Stats Cards */}
               <Group grow mb="md">
                  <Paper
                     p="md"
                     radius="sm"
                     style={{
                        background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
                        border: "1px solid #699269",
                     }}
                  >
                     <Group gap="xs">
                        <ThemeIcon size="lg" radius="sm" style={{ background: "#699269" }}>
                           <IconCreditCard size={20} />
                        </ThemeIcon>
                        <div>
                           <Text size="xs" fw={500} c="#558B2F">
                              Total Requests
                           </Text>
                           <Text size="xl" fw={700} c="#699269">
                              {stats.total}
                           </Text>
                        </div>
                     </Group>
                  </Paper>

                  <Paper
                     p="md"
                     radius="sm"
                     style={{
                        background: "linear-gradient(135deg, #FFF9E6 0%, #FFF3CC 100%)",
                        border: "1px solid #F59E0B",
                     }}
                  >
                     <Group gap="xs">
                        <ThemeIcon size="lg" radius="sm" style={{ background: "#F59E0B" }}>
                           <IconClock size={20} />
                        </ThemeIcon>
                        <div>
                           <Text size="xs" fw={500} c="#92400E">
                              Pending Review
                           </Text>
                           <Text size="xl" fw={700} c="#F59E0B">
                              {stats.pending}
                           </Text>
                        </div>
                     </Group>
                  </Paper>

                  <Paper
                     p="md"
                     radius="sm"
                     style={{
                        background: "linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)",
                        border: "1px solid #22C55E",
                     }}
                  >
                     <Group gap="xs">
                        <ThemeIcon size="lg" radius="sm" style={{ background: "#22C55E" }}>
                           <IconCheck size={20} />
                        </ThemeIcon>
                        <div>
                           <Text size="xs" fw={500} c="#166534">
                              Approved
                           </Text>
                           <Text size="xl" fw={700} c="#22C55E">
                              {stats.approved}
                           </Text>
                        </div>
                     </Group>
                  </Paper>

                  <Paper
                     p="md"
                     radius="sm"
                     style={{
                        background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
                        border: "1px solid #6366F1",
                     }}
                  >
                     <Group gap="xs">
                        <ThemeIcon size="lg" radius="sm" style={{ background: "#6366F1" }}>
                           <IconCurrencyNaira size={20} />
                        </ThemeIcon>
                        <div>
                           <Text size="xs" fw={500} c="#312E81">
                              Total Amount
                           </Text>
                           <Text size="lg" fw={700} c="#6366F1">
                              {formatCurrency(stats.totalAmount)}
                           </Text>
                        </div>
                     </Group>
                  </Paper>
               </Group>

               {/* Search Bar */}
               <TextInput
                  placeholder="Search by email, name, or loan ID..."
                  leftSection={<IconSearch size={18} />}
                  size="md"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ maxWidth: "400px" }}
               />
            </motion.div>

            {/* Tabs for filtering */}
            <Tabs value={activeTab} onChange={setActiveTab} color="green">
               <Tabs.List>
                  <Tabs.Tab value="all">All Requests</Tabs.Tab>
                  <Tabs.Tab value="pending" leftSection={<IconClock size={16} />}>
                     Pending
                  </Tabs.Tab>
                  <Tabs.Tab value="approved" leftSection={<IconCheck size={16} />}>
                     Approved
                  </Tabs.Tab>
                  <Tabs.Tab value="rejected" leftSection={<IconX size={16} />}>
                     Rejected
                  </Tabs.Tab>
               </Tabs.List>
            </Tabs>

            {/* Loading State */}
            {isLoading ? (
               <Paper
                  shadow="sm"
                  p="xl"
                  radius="sm"
                  style={{
                     background: "white",
                     border: "2px solid #e5e7eb",
                  }}
               >
                  <Stack align="center" gap="lg" py="xl">
                     <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#699269]"></div>
                     <Text size="lg" c="dimmed">
                        Loading loan requests...
                     </Text>
                  </Stack>
               </Paper>
            ) : !loansResponse?.data || loansResponse.data.length === 0 ? (
               /* Empty State */
               <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
               >
                  <Paper
                     shadow="md"
                     p="xl"
                     radius="sm"
                     style={{
                        background: "linear-gradient(135deg, #f9fafb 0%, #E8F5E9 100%)",
                        border: "2px solid #e5e7eb",
                     }}
                  >
                     <Stack align="center" gap="xl" py="xl">
                        <motion.div
                           initial={{ scale: 0 }}
                           animate={{ scale: 1 }}
                           transition={{ delay: 0.2, type: "spring" }}
                        >
                           <ThemeIcon
                              size={120}
                              radius="md"
                              style={{
                                 background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
                              }}
                           >
                              <IconCreditCard size={60} style={{ color: "#699269" }} />
                           </ThemeIcon>
                        </motion.div>
                        <div style={{ textAlign: "center" }}>
                           <Title order={2} fw={700} mb="sm" style={{ color: "#111827" }}>
                              No loan requests found
                           </Title>
                           <Text size="lg" c="dimmed" style={{ maxWidth: "500px" }}>
                              There are no loan requests matching your filters.
                           </Text>
                        </div>
                     </Stack>
                  </Paper>
               </motion.div>
            ) : (
               /* Loan Requests Table */
               <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
               >
                  <Paper
                     shadow="sm"
                     radius="sm"
                     style={{
                        background: "white",
                        border: "2px solid #e5e7eb",
                        overflow: "hidden",
                     }}
                  >
                     <ScrollArea>
                        <Table striped highlightOnHover withTableBorder withColumnBorders style={{ minWidth: 1200 }}>
                           <Table.Thead>
                              <Table.Tr style={{ background: "#F9FAFB" }}>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Loan</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>User</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Amount</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Duration</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Purpose</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Status</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369" }}>Applied</Table.Th>
                                 <Table.Th style={{ fontWeight: 700, color: "#679369", textAlign: "center" }}>
                                    Actions
                                 </Table.Th>
                              </Table.Tr>
                           </Table.Thead>
                           <Table.Tbody>
                              {loansResponse.data.map((loan) => {
                                 const StatusIcon = getStatusIcon(loan.status);
                                 return (
                                    <Table.Tr key={loan.id}>
                                       <Table.Td>
                                          <Text size="sm" fw={600} c="dimmed">
                                             {loan.loanID.name}
                                          </Text>
                                       </Table.Td>
                                       <Table.Td>
                                          <Text size="sm">{loan.userID.email}</Text>
                                       </Table.Td>
                                       <Table.Td>
                                          <Text size="sm" fw={700} style={{ color: "#679369" }}>
                                             {formatCurrency(loan.amount)}
                                          </Text>
                                       </Table.Td>
                                       <Table.Td>
                                          <Text size="sm">{loan.duration} months</Text>
                                       </Table.Td>
                                       <Table.Td>
                                          <Text size="sm" lineClamp={2} style={{ maxWidth: 250 }}>
                                             {getEllipsisFromString(loan.purpose, 40)}
                                          </Text>
                                       </Table.Td>
                                       <Table.Td>
                                          <Badge size="md" radius="sm" color={getStatusColor(loan.status)}>
                                             <Group gap={4}>
                                                <span>{loan.status}</span>
                                             </Group>
                                          </Badge>
                                       </Table.Td>
                                       <Table.Td>
                                          <Text size="sm">{formatDate(loan.createdAt)}</Text>
                                       </Table.Td>
                                       <Table.Td>
                                          <Group justify="center" gap="xs">
                                             <Menu shadow="md" width={200}>
                                                <Menu.Target>
                                                   <ActionIcon
                                                      variant="subtle"
                                                      color="gray"
                                                      disabled={updatingLoanId === loan.id}
                                                   >
                                                      <IconDots size={20} />
                                                   </ActionIcon>
                                                </Menu.Target>

                                                <Menu.Dropdown>
                                                   <Menu.Item
                                                      leftSection={<IconEye size={16} />}
                                                      onClick={() => handleViewDetails(loan)}
                                                   >
                                                      View Details
                                                   </Menu.Item>
                                                   {loan.status === "pending" && (
                                                      <>
                                                         <Menu.Item
                                                            leftSection={<IconCheck size={16} />}
                                                            color="green"
                                                            onClick={() => handleUpdateLoanStatus(loan.id, "approved")}
                                                            disabled={updatingLoanId === loan.id}
                                                         >
                                                            Approve Loan
                                                         </Menu.Item>
                                                         <Menu.Item
                                                            leftSection={<IconX size={16} />}
                                                            color="red"
                                                            onClick={() => handleUpdateLoanStatus(loan.id, "rejected")}
                                                            disabled={updatingLoanId === loan.id}
                                                         >
                                                            Reject Loan
                                                         </Menu.Item>
                                                      </>
                                                   )}
                                                </Menu.Dropdown>
                                             </Menu>
                                          </Group>
                                       </Table.Td>
                                    </Table.Tr>
                                 );
                              })}
                           </Table.Tbody>
                        </Table>
                     </ScrollArea>
                  </Paper>
               </motion.div>
            )}

            {/* Pagination */}
            {loansResponse?.data && loansResponse.data.length > 0 && totalPages > 1 && (
               <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                  <Paper
                     p="lg"
                     radius="sm"
                     style={{
                        background: "white",
                        border: "2px solid #e5e7eb",
                     }}
                  >
                     <Group justify="center">
                        <Pagination
                           total={totalPages}
                           value={pagination.page}
                           onChange={(page) => setPagination({ ...pagination, page })}
                           size="lg"
                           radius="sm"
                           color="green"
                           styles={{
                              control: {
                                 border: "2px solid #e5e7eb",
                                 "&[data-active]": {
                                    background: "linear-gradient(135deg, #699269 0%, #81C784 100%)",
                                    border: "2px solid #699269",
                                    fontWeight: 700,
                                 },
                                 "&:hover": {
                                    background: "#E8F5E9",
                                 },
                              },
                           }}
                        />
                     </Group>
                     <Text size="sm" c="dimmed" ta="center" mt="md">
                        Showing page {pagination.page} of {totalPages} • {loansResponse.totalRecords || 0} total
                        requests
                     </Text>
                  </Paper>
               </motion.div>
            )}
         </Stack>

         {/* Loan Details Modal */}
         <Modal
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            title={
               <Group gap="sm">
                  <ThemeIcon
                     size="lg"
                     radius="md"
                     style={{ background: "linear-gradient(135deg, #699269 0%, #81C784 100%)" }}
                  >
                     <IconCreditCard size={24} />
                  </ThemeIcon>
                  <div>
                     <Text size="xl" fw={700} style={{ color: "#679369" }}>
                        Loan Request Details
                     </Text>
                     <Text size="sm" c="dimmed">
                        Review complete loan information
                     </Text>
                  </div>
               </Group>
            }
            size="xl"
            padding="xl"
            centered
            styles={{
               content: {
                  background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
               },
               header: {
                  borderBottom: "2px solid #e5e7eb",
                  paddingBottom: "1rem",
               },
            }}
         >
            {selectedLoan && (
               <Stack gap="xl">
                  <Group justify="space-between" align="center">
                     <Text size="sm" c="dimmed">
                        Review complete loan information
                     </Text>
                     <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => {
                           setModalOpened(false);
                           navigate(`/dashboard/loan-requests/${selectedLoan.id}`);
                        }}
                     >
                        View Details
                     </Button>
                  </Group>
                  {/* Status Badge */}
                  <motion.div
                     initial={{ opacity: 0, y: -10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3 }}
                  >
                     <br />
                     <Paper
                        p="md"
                        radius="md"
                        style={{
                           background: `linear-gradient(135deg, ${
                              selectedLoan.status === "approved"
                                 ? "#DCFCE7 0%, #BBF7D0 100%"
                                 : selectedLoan.status === "rejected"
                                 ? "#FEE2E2 0%, #FECACA 100%"
                                 : "#FFF9E6 0%, #FFF3CC 100%"
                           })`,
                           border: `2px solid ${
                              selectedLoan.status === "approved"
                                 ? "#22C55E"
                                 : selectedLoan.status === "rejected"
                                 ? "#EF4444"
                                 : "#F59E0B"
                           }`,
                        }}
                     >
                        <Group justify="space-between">
                           <Group gap="sm">
                              <ThemeIcon
                                 size="xl"
                                 radius="md"
                                 style={{
                                    background:
                                       selectedLoan.status === "approved"
                                          ? "#22C55E"
                                          : selectedLoan.status === "rejected"
                                          ? "#EF4444"
                                          : "#F59E0B",
                                 }}
                              >
                                 {selectedLoan.status === "approved" ? (
                                    <IconCheck size={28} />
                                 ) : selectedLoan.status === "rejected" ? (
                                    <IconX size={28} />
                                 ) : (
                                    <IconClock size={28} />
                                 )}
                              </ThemeIcon>
                              <div>
                                 <Text size="xs" tt="uppercase" fw={600} c="dimmed">
                                    Status
                                 </Text>
                                 <Badge
                                    size="lg"
                                    radius="sm"
                                    color={getStatusColor(selectedLoan.status)}
                                    tt="uppercase"
                                    fw={700}
                                 >
                                    {selectedLoan.status}
                                 </Badge>
                              </div>
                           </Group>
                           <div style={{ textAlign: "right" }}>
                              <Text size="xs" c="dimmed">
                                 Applied on
                              </Text>
                              <Text size="sm" fw={600}>
                                 {formatDate(selectedLoan.createdAt)}
                              </Text>
                           </div>
                        </Group>
                     </Paper>
                  </motion.div>

                  <Divider label="User Information" labelPosition="center" />

                  {/* User Details */}
                  <motion.div
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.1 }}
                  >
                     <Paper
                        p="lg"
                        radius="md"
                        style={{
                           background: "white",
                           border: "2px solid #e5e7eb",
                        }}
                     >
                        <Grid gutter="lg">
                           <Grid.Col span={{ base: 12, sm: 6 }}>
                              <Group gap="sm">
                                 <ThemeIcon size="lg" radius="md" color="blue" variant="light">
                                    <IconMail size={20} />
                                 </ThemeIcon>
                                 <div>
                                    <Text size="xs" c="dimmed" fw={500}>
                                       Email Address
                                    </Text>
                                    <Text size="sm" fw={600}>
                                       {selectedLoan.userID.email}
                                    </Text>
                                 </div>
                              </Group>
                           </Grid.Col>
                           <Grid.Col span={{ base: 12, sm: 6 }}>
                              <Group gap="sm">
                                 <ThemeIcon size="lg" radius="md" color="teal" variant="light">
                                    <IconPhone size={20} />
                                 </ThemeIcon>
                                 <div>
                                    <Text size="xs" c="dimmed" fw={500}>
                                       Phone Number
                                    </Text>
                                    <Text size="sm" fw={600}>
                                       {selectedLoan.userID.phone}
                                    </Text>
                                 </div>
                              </Group>
                           </Grid.Col>
                        </Grid>
                     </Paper>
                  </motion.div>

                  <Divider label="Loan Plan Details" labelPosition="center" />

                  {/* Loan Plan Information */}
                  <motion.div
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.2 }}
                  >
                     <Paper
                        p="lg"
                        radius="md"
                        style={{
                           background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
                           border: "2px solid #699269",
                        }}
                     >
                        <Stack gap="md">
                           <Group justify="space-between" align="flex-start">
                              <div>
                                 <Text size="xs" tt="uppercase" c="dimmed" fw={600}>
                                    Loan Plan
                                 </Text>
                                 <Title order={3} style={{ color: "#679369" }}>
                                    {selectedLoan.loanID.name}
                                 </Title>
                              </div>
                              <ThemeIcon size="xl" radius="md" style={{ background: "#679369" }}>
                                 <IconTrendingUp size={28} />
                              </ThemeIcon>
                           </Group>

                           <Text size="sm" c="dimmed">
                              {selectedLoan.loanID.description}
                           </Text>

                           <Grid gutter="md">
                              <Grid.Col span={{ base: 12, sm: 6 }}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "white",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Text size="xs" c="dimmed" fw={500}>
                                       Interest Rate
                                    </Text>
                                    <Text size="xl" fw={700} style={{ color: "#679369" }}>
                                       {selectedLoan.loanID.interestRate}%
                                    </Text>
                                 </Box>
                              </Grid.Col>
                              <Grid.Col span={{ base: 12, sm: 6 }}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "white",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Text size="xs" c="dimmed" fw={500}>
                                       Max Period
                                    </Text>
                                    <Text size="xl" fw={700} style={{ color: "#679369" }}>
                                       {selectedLoan.loanID.maximumPeriod} months
                                    </Text>
                                 </Box>
                              </Grid.Col>
                              <Grid.Col span={{ base: 12, sm: 6 }}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "white",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Text size="xs" c="dimmed" fw={500}>
                                       Min Amount
                                    </Text>
                                    <Text size="lg" fw={700} style={{ color: "#679369" }}>
                                       {formatCurrency(selectedLoan.loanID.minimumAmount)}
                                    </Text>
                                 </Box>
                              </Grid.Col>
                              <Grid.Col span={{ base: 12, sm: 6 }}>
                                 <Box
                                    p="md"
                                    style={{
                                       background: "white",
                                       borderRadius: "8px",
                                       border: "1px solid #e5e7eb",
                                    }}
                                 >
                                    <Text size="xs" c="dimmed" fw={500}>
                                       Max Amount
                                    </Text>
                                    <Text size="lg" fw={700} style={{ color: "#679369" }}>
                                       {formatCurrency(selectedLoan.loanID.maximumAmount)}
                                    </Text>
                                 </Box>
                              </Grid.Col>
                           </Grid>

                           {selectedLoan.loanID.benefits && selectedLoan.loanID.benefits.length > 0 && (
                              <>
                                 <Divider />
                                 <div>
                                    <Text size="sm" fw={600} mb="xs" style={{ color: "#679369" }}>
                                       Benefits
                                    </Text>
                                    <Stack gap="xs">
                                       {selectedLoan.loanID.benefits.map((benefit, index) => (
                                          <Group key={index} gap="xs">
                                             <IconCheck size={16} style={{ color: "#679369" }} />
                                             <Text size="sm">{benefit}</Text>
                                          </Group>
                                       ))}
                                    </Stack>
                                 </div>
                              </>
                           )}
                        </Stack>
                     </Paper>
                  </motion.div>

                  <Divider label="Request Details" labelPosition="center" />

                  {/* Request Specific Information */}
                  <motion.div
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.3 }}
                  >
                     <Paper
                        p="lg"
                        radius="md"
                        style={{
                           background: "white",
                           border: "2px solid #e5e7eb",
                        }}
                     >
                        <Grid gutter="lg">
                           <Grid.Col span={{ base: 12, sm: 6 }}>
                              <Paper
                                 p="md"
                                 radius="md"
                                 style={{
                                    background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
                                    border: "1px solid #6366F1",
                                 }}
                              >
                                 <Group gap="sm">
                                    <ThemeIcon size="xl" radius="md" style={{ background: "#6366F1" }}>
                                       <IconCurrencyNaira size={24} />
                                    </ThemeIcon>
                                    <div>
                                       <Text size="xs" c="dimmed" fw={500}>
                                          Requested Amount
                                       </Text>
                                       <Text size="xl" fw={700} style={{ color: "#6366F1" }}>
                                          {formatCurrency(selectedLoan.amount)}
                                       </Text>
                                    </div>
                                 </Group>
                              </Paper>
                           </Grid.Col>
                           <Grid.Col span={{ base: 12, sm: 6 }}>
                              <Paper
                                 p="md"
                                 radius="md"
                                 style={{
                                    background: "linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)",
                                    border: "1px solid #F59E0B",
                                 }}
                              >
                                 <Group gap="sm">
                                    <ThemeIcon size="xl" radius="md" style={{ background: "#F59E0B" }}>
                                       <IconCalendar size={24} />
                                    </ThemeIcon>
                                    <div>
                                       <Text size="xs" c="dimmed" fw={500}>
                                          Duration
                                       </Text>
                                       <Text size="xl" fw={700} style={{ color: "#F59E0B" }}>
                                          {selectedLoan.duration} months
                                       </Text>
                                    </div>
                                 </Group>
                              </Paper>
                           </Grid.Col>
                           <Grid.Col span={12}>
                              <Box
                                 p="md"
                                 style={{
                                    background: "#f9fafb",
                                    borderRadius: "8px",
                                    border: "1px solid #e5e7eb",
                                 }}
                              >
                                 <Group gap="sm" align="flex-start">
                                    <ThemeIcon size="lg" radius="md" color="gray" variant="light">
                                       <IconFileText size={20} />
                                    </ThemeIcon>
                                    <div style={{ flex: 1 }}>
                                       <Text size="xs" c="dimmed" fw={500} mb="xs">
                                          Loan Purpose
                                       </Text>
                                       <Text size="sm">{selectedLoan.purpose}</Text>
                                    </div>
                                 </Group>
                              </Box>
                           </Grid.Col>
                        </Grid>

                        {/* Calculation Summary */}
                        <Divider my="lg" />
                        <Paper
                           p="md"
                           radius="md"
                           style={{
                              background: "linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)",
                              border: "1px solid #D1D5DB",
                           }}
                        >
                           <Text size="sm" fw={600} mb="sm" c="dimmed">
                              Loan Calculation Summary
                           </Text>
                           <Stack gap="xs">
                              <Group justify="space-between">
                                 <Text size="sm">Principal Amount:</Text>
                                 <Text size="sm" fw={600}>
                                    {formatCurrency(selectedLoan.amount)}
                                 </Text>
                              </Group>
                              <Group justify="space-between">
                                 <Text size="sm">Interest ({selectedLoan.loanID.interestRate}%):</Text>
                                 <Text size="sm" fw={600}>
                                    {formatCurrency((selectedLoan.amount * selectedLoan.loanID.interestRate) / 100)}
                                 </Text>
                              </Group>
                              <Divider />
                              <Group justify="space-between">
                                 <Text size="md" fw={700}>
                                    Total Repayment:
                                 </Text>
                                 <Text size="md" fw={700} style={{ color: "#679369" }}>
                                    {formatCurrency(
                                       selectedLoan.amount +
                                          (selectedLoan.amount * selectedLoan.loanID.interestRate) / 100
                                    )}
                                 </Text>
                              </Group>
                              <Group justify="space-between">
                                 <Text size="sm" c="dimmed">
                                    Monthly Payment:
                                 </Text>
                                 <Text size="sm" fw={600} c="dimmed">
                                    {formatCurrency(
                                       (selectedLoan.amount +
                                          (selectedLoan.amount * selectedLoan.loanID.interestRate) / 100) /
                                          selectedLoan.duration
                                    )}
                                 </Text>
                              </Group>
                           </Stack>
                        </Paper>
                     </Paper>
                  </motion.div>

                  {/* Direct Debit Summary (if any) */}
                  {selectedLoan.scheduledDirectDebitAmount && (
                     <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.35 }}
                     >
                        <Paper
                           p="md"
                           radius="md"
                           style={{
                              background: "linear-gradient(135deg, #ECFEFF 0%, #CFFAFE 100%)",
                              border: "1px solid #06B6D4",
                           }}
                        >
                           <Group justify="space-between" align="center">
                              <div>
                                 <Text size="sm" fw={600} style={{ color: "#0E7490" }}>
                                    Scheduled Direct Debit
                                 </Text>
                                 <Text size="xs" c="dimmed">
                                    Amount to debit:{" "}
                                    <strong>
                                       {formatCurrency(selectedLoan.scheduledDirectDebitAmount)}
                                    </strong>
                                 </Text>
                              </div>
                           </Group>
                        </Paper>
                     </motion.div>
                  )}

                  {/* Action Buttons */}
                  {selectedLoan.status === "pending" && (
                     <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.4 }}
                     >
                        <Group justify="flex-end" gap="sm">
                           <Button
                              variant="outline"
                              color="red"
                              size="md"
                              leftSection={<IconX size={20} />}
                              onClick={() => handleUpdateLoanStatus(selectedLoan.id, "rejected")}
                              loading={updatingLoanId === selectedLoan.id}
                           >
                              Reject Loan
                           </Button>
                           <Button
                              variant="gradient"
                              gradient={{ from: "#699269", to: "#81C784", deg: 135 }}
                              size="md"
                              leftSection={<IconCheck size={20} />}
                              onClick={() => handleUpdateLoanStatus(selectedLoan.id, "approved")}
                              loading={updatingLoanId === selectedLoan.id}
                           >
                              Approve Loan
                           </Button>
                        </Group>
                     </motion.div>
                  )}
               </Stack>
            )}
         </Modal>

         {/* Loan Approval Modal */}
         <Modal
            opened={approvalModalOpened}
            onClose={() => {
               setApprovalModalOpened(false);
               setLoanToApprove(null);
               setRequiresOtp(false);
               setOtp("");
               setTransferCode(null);
            }}
            title={
               <Group gap="sm">
                  <ThemeIcon
                     size="lg"
                     radius="md"
                     style={{ background: "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)" }}
                  >
                     <IconShieldCheck size={24} />
                  </ThemeIcon>
                  <div>
                     <Text size="xl" fw={700} style={{ color: "#22C55E" }}>
                        Approve Loan
                     </Text>
                     <Text size="sm" c="dimmed">
                        Review bank account and approve the loan request
                     </Text>
                  </div>
               </Group>
            }
            size="xl"
            padding="xl"
            centered
            styles={{
               content: {
                  background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
               },
               header: {
                  borderBottom: "2px solid #e5e7eb",
                  paddingBottom: "1rem",
               },
            }}
         >
            {loanToApprove && (
               <Stack gap="xl">
                  {/* User Bank Account Information */}
                  <motion.div
                     initial={{ opacity: 0, y: -10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3 }}
                  >
                     <Paper
                        p="lg"
                        radius="md"
                        style={{
                           background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
                           border: "2px solid #3B82F6",
                        }}
                     >
                        <Group gap="md" align="flex-start">
                           <ThemeIcon size="xl" radius="md" style={{ background: "#3B82F6" }}>
                              <IconBuildingBank size={28} />
                           </ThemeIcon>
                           <div style={{ flex: 1 }}>
                              <Text size="sm" fw={600} mb="md" style={{ color: "#3B82F6" }}>
                                 Beneficiary Bank Account
                              </Text>
                              {(loanToApprove.userID as any)?.bankAccount ? (
                                 <Stack gap="sm">
                                    <Box
                                       p="md"
                                       style={{
                                          background: "white",
                                          borderRadius: "8px",
                                          border: "1px solid #e5e7eb",
                                       }}
                                    >
                                       <Grid gutter="md">
                                          <Grid.Col span={{ base: 12, sm: 6 }}>
                                             <Text size="xs" c="dimmed" fw={500}>
                                                Account Name
                                             </Text>
                                             <Text size="md" fw={700} style={{ color: "#3B82F6" }}>
                                                {(loanToApprove.userID as any).bankAccount.accountName || "N/A"}
                                             </Text>
                                          </Grid.Col>
                                          <Grid.Col span={{ base: 12, sm: 6 }}>
                                             <Text size="xs" c="dimmed" fw={500}>
                                                Account Number
                                             </Text>
                                             <Text size="md" fw={700} style={{ color: "#3B82F6" }}>
                                                {(loanToApprove.userID as any).bankAccount.accountNumber}
                                             </Text>
                                          </Grid.Col>
                                          <Grid.Col span={{ base: 12, sm: 6 }}>
                                             <Text size="xs" c="dimmed" fw={500}>
                                                Bank Name
                                             </Text>
                                             <Text size="md" fw={600}>
                                                {(loanToApprove.userID as any).bankAccount.bankName || "N/A"}
                                             </Text>
                                          </Grid.Col>
                                          <Grid.Col span={{ base: 12, sm: 6 }}>
                                             <Text size="xs" c="dimmed" fw={500}>
                                                Bank Code
                                             </Text>
                                             <Text size="md" fw={600}>
                                                {(loanToApprove.userID as any).bankAccount.bankCode}
                                             </Text>
                                          </Grid.Col>
                                       </Grid>
                                    </Box>
                                 </Stack>
                              ) : (
                                 <Paper
                                    p="lg"
                                    radius="md"
                                    style={{
                                       background: "linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)",
                                       border: "2px solid #EF4444",
                                    }}
                                 >
                                    <Group gap="sm">
                                       <ThemeIcon size="xl" radius="md" style={{ background: "#EF4444" }}>
                                          <IconAlertCircle size={28} />
                                       </ThemeIcon>
                                       <div>
                                          <Text size="md" fw={700} style={{ color: "#EF4444" }}>
                                             Bank Account Not Found
                                          </Text>
                                          <Text size="sm" c="dimmed">
                                             User has not set up a bank account. Please ask them to add their bank
                                             account details before approving this loan.
                                          </Text>
                                       </div>
                                    </Group>
                                 </Paper>
                              )}
                           </div>
                        </Group>
                     </Paper>
                  </motion.div>

                  {/* Action Buttons */}
                  <motion.div
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.3, delay: 0.2 }}
                  >
                     <Group justify="flex-end" gap="sm">
                        <Button
                           variant="outline"
                           color="gray"
                           size="md"
                           onClick={() => {
                              setApprovalModalOpened(false);
                              setLoanToApprove(null);
                              setRequiresOtp(false);
                              setOtp("");
                              setTransferCode(null);
                           }}
                           disabled={updatingLoanId === loanToApprove.id}
                        >
                           Cancel
                        </Button>
                        {loanToApprove.status === "pending" && (
                           <Button
                              variant="gradient"
                              gradient={{ from: "#22C55E", to: "#16A34A", deg: 135 }}
                              size="md"
                              leftSection={<IconCheck size={20} />}
                              onClick={handleApproveLoan}
                              loading={updatingLoanId === loanToApprove.id}
                              disabled={
                                 !(loanToApprove.userID as any)?.bankAccount ||
                                 !(loanToApprove.userID as any)?.bankAccount?.accountNumber
                              }
                           >
                              Approve Loan
                           </Button>
                        )}
                     </Group>
                  </motion.div>
               </Stack>
            )}
         </Modal>
      </Container>
   );
}
